import React from 'react';
import { Link } from 'react-router-dom';
import useTrackRescuePageView from '../../hooks/useTrackRescuePageView';
import styles from './Terms.module.scss';

export default function Terms() {
  // This route is mounted outside RescuePortal, so the router-level pageview
  // tracker in RescuePortal.tsx never fires here.
  useTrackRescuePageView('/rescue/terms', 'Terms');

  return (
    <div className={styles.page}>
      {/* Hero */}
      <div className={styles.hero}>
        <Link to="/rescue/login" className={styles.backLink}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="15 18 9 12 15 6" />
          </svg>
          Back to Portal
        </Link>
        <h1>Fi Rescue Program Terms of Service</h1>
        <p className={styles.heroSubtitle}>Terms of service for the Fi rescue partner program.</p>
        <p className={styles.effectiveDate}>Effective upon electronic acceptance via the rescue portal</p>
      </div>

      {/* Agreement Content */}
      <div className={styles.content}>
        {/* Section 1 */}
        <h2 className={styles.sectionTitle}>1. Preamble &amp; Definitions</h2>

        <p className={styles.clause}>
          <span className={styles.clauseNumber}>1.1</span> These Fi Rescue Program Terms of Service
          (&ldquo;Agreement&rdquo;) is entered into by and between Barking Labs, Corp, d/b/a Fi, a Delaware corporation,
          with its principal place of business at 419 Lafayette St &mdash; 2nd Fl, New York, NY 10003 (&ldquo;Fi,&rdquo;
          &ldquo;we,&rdquo; &ldquo;us&rdquo;), and the rescue organization identified during portal enrollment
          (&ldquo;Rescue,&rdquo; &ldquo;you,&rdquo; &ldquo;your&rdquo;).
        </p>

        <p className={styles.clause}>
          <span className={styles.clauseNumber}>1.2</span> This Agreement becomes effective on the date of electronic
          acceptance via the rescue portal at nano.fitracking.com/rescue (&ldquo;Effective Date&rdquo;).
        </p>

        <p className={styles.clause}>
          <span className={styles.clauseNumber}>1.3</span> <strong>Definitions.</strong> The following terms have these
          meanings throughout this Agreement:
        </p>

        <table className={styles.definitionsTable}>
          <thead>
            <tr>
              <th>Term</th>
              <th>Definition</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Program</td>
              <td>
                The Fi Gives Back Program, through which Fi provides products and services to qualified rescue
                organizations
              </td>
            </tr>
            <tr>
              <td>Fi Nano Chip</td>
              <td>Fi Nano microchip, a pet identification microchip manufactured or distributed by Fi</td>
            </tr>
            <tr>
              <td>Fi Device</td>
              <td>Fi GPS tracking collar or collar module, including all hardware and firmware</td>
            </tr>
            <tr>
              <td>Membership</td>
              <td>Active Fi GPS tracking subscription associated with a Fi Device</td>
            </tr>
            <tr>
              <td>GWA</td>
              <td>
                Gift with Adoption &mdash; a discounted or complimentary Fi product offering provided to verified
                adopters
              </td>
            </tr>
            <tr>
              <td>T2A</td>
              <td>
                Transfer to Adopter &mdash; the process of transferring Fi Device ownership and Membership from Rescue
                to an adopter through the Portal
              </td>
            </tr>
            <tr>
              <td>Portal</td>
              <td>
                The Fi rescue portal at nano.fitracking.com/rescue, including all tools, dashboards, and workflows
              </td>
            </tr>
            <tr>
              <td>Promo Code</td>
              <td>A unique discount code assigned to Rescue for GWA redemption</td>
            </tr>
            <tr>
              <td>Custom PDP</td>
              <td>A co-branded product detail page on shop.fitracking.com specific to Rescue</td>
            </tr>
            <tr>
              <td>Adopter</td>
              <td>An individual who has completed a verified adoption of a cat or dog from Rescue</td>
            </tr>
            <tr>
              <td>Foster</td>
              <td>An individual temporarily housing an animal on Rescue&rsquo;s behalf</td>
            </tr>
          </tbody>
        </table>

        {/* Section 2 */}
        <h2 className={styles.sectionTitle}>2. Eligibility &amp; Enrollment</h2>

        <p className={styles.clause}>
          <span className={styles.clauseNumber}>2.1</span> <strong>Primary Contact.</strong> Rescue shall designate a
          primary contact person (name, email address, and phone number) responsible for device management, T2A
          coordination, and all Program-related communications with Fi. Rescue shall notify Fi within seven (7) days of
          any change in primary contact.
        </p>

        {/* Section 3 */}
        <h2 className={styles.sectionTitle}>3. Program Components</h2>

        <h3 className={styles.subsectionTitle}>3A. Fi Nano Microchips</h3>

        <p className={styles.clause}>
          <span className={styles.clauseNumber}>3A.1</span> Fi will provide Fi Nano Chips to Rescue at no cost, in
          quantities reasonably requested by Rescue through Fi&rsquo;s designated order form.
        </p>
        <p className={styles.clause}>
          <span className={styles.clauseNumber}>3A.2</span> All Fi Nano Chips must be registered using the Fi Nano
          database or through an approved integration.
        </p>
        <p className={styles.clause}>
          <span className={styles.clauseNumber}>3A.3</span> Rescue shall not resell, redistribute, donate to
          unaffiliated organizations, or use Fi Nano Chips for any commercial purpose.
        </p>
        <p className={styles.clause}>
          <span className={styles.clauseNumber}>3A.4</span> All Fi Nano Chip orders must be placed through Fi&rsquo;s
          designated order form. Fi reserves the right to set reasonable limits on order frequency and quantity based on
          Rescue&rsquo;s implantation volume.
        </p>

        <h3 className={styles.subsectionTitle}>3B. GPS Tracking Collars</h3>

        <p className={styles.clause}>
          <span className={styles.clauseNumber}>3B.1</span> <strong>Ownership.</strong> All Fi Devices provided under
          this Program are and remain the sole property of Fi, other than to the degree ownership transfers to a
          consumer as defined by terms between Fi and them. Fi Devices are provided on a bailment basis &mdash; they are
          loaned, not given, gifted, or sold. No transfer of title occurs until a completed T2A transaction in which the
          adopter purchases a Membership.
        </p>
        <p className={styles.clause}>
          <span className={styles.clauseNumber}>3B.2</span> <strong>Care &amp; Accountability.</strong> Rescue shall
          exercise reasonable care in storing, handling, and deploying Fi Devices. Rescue shall maintain accurate
          inventory records through the Portal and shall be able to account for all Fi Devices in its possession at any
          time upon Fi&rsquo;s request.
        </p>
        <p className={styles.clause}>
          <span className={styles.clauseNumber}>3B.3</span> <strong>Return Obligation.</strong> Rescue shall return all
          Fi Devices in its possession within thirty (30) days of termination of this Agreement or upon Fi&rsquo;s
          written request. Fi shall provide prepaid shipping labels for all authorized returns.
        </p>
        <p className={styles.clause}>
          <span className={styles.clauseNumber}>3B.4</span> <strong>Restrictions.</strong> Rescue shall not:
        </p>
        <ul className={styles.subList}>
          <li>(a) Modify, disassemble, tamper with, or reverse-engineer any Fi Device or its firmware;</li>
          <li>
            (b) Transfer Fi Devices to any person or organization other than through the T2A process or with Fi&rsquo;s
            prior written consent;
          </li>
          <li>
            (c) Use Fi Devices for any purpose other than temporary placement on foster or shelter animals pending
            adoption, or at Fi&rsquo;s direction.
          </li>
        </ul>

        <p className={styles.clause}>
          <span className={styles.clauseNumber}>3B.5</span> <strong>Lost or Stolen Devices.</strong> Rescue shall report
          any Fi Device that is lost, stolen, or unaccounted for within seven (7) days of discovery. Persistent failure
          to account for Fi Devices may result in reduced device allocation under Section 3D.5 or other consequences at
          Fi&rsquo;s discretion.
        </p>
        <p className={styles.clause}>
          <span className={styles.clauseNumber}>3B.6</span> <strong>Remote Management.</strong> Fi may remotely disable,
          lock, or modify Fi Devices that are reported lost/stolen, associated with terminated accounts, or reasonably
          believed to be misused. Fi may provide notice to Rescue before or promptly after taking such action, but
          reserves the right to act immediately and/or unilaterally when necessary or commercially reasonable.
        </p>

        <h3 className={styles.subsectionTitle}>3C. Gift with Adoption (GWA)</h3>

        <p className={styles.clause}>
          <span className={styles.clauseNumber}>3C.1</span> Fi will provide Rescue with a Custom PDP and unique Promo
          Code for distribution to verified Adopters as part of the adoption process.
        </p>
        <p className={styles.clause}>
          <span className={styles.clauseNumber}>3C.2</span> <strong>Promo Code Restrictions.</strong> The Promo Code is
          limited to verified Adopters who have completed an adoption through Rescue, or employees, staff or friends of
          Rescue. Rescue shall not:
        </p>
        <ul className={styles.subList}>
          <li>(a) Post, share, or distribute the Promo Code on any public forum, website, or social media platform;</li>
          <li>
            (b) Provide the Promo Code to any individual who has not completed a verified adoption through Rescue, other
            than affiliated employees, staff or supporters/friends of the Rescue;
          </li>
          <li>(c) Share the Promo Code with other rescue organizations.</li>
        </ul>

        <p className={styles.clause}>
          <span className={styles.clauseNumber}>3C.3</span> <strong>Monitoring &amp; Enforcement.</strong> Fi monitors
          Promo Code redemption patterns for anomalies. If Fi detects evidence of abuse &mdash; including but not
          limited to unusual redemption volume, geographic dispersion inconsistent with Rescue&rsquo;s service area, or
          redemptions by non-Adopters &mdash; Fi may suspend the Promo Code immediately and without prior notice.
        </p>
        <p className={styles.clause}>
          <span className={styles.clauseNumber}>3C.4</span> <strong>Modifications.</strong> Fi may modify GWA pricing,
          discount amounts, eligible products, and other terms at our discretion. Such modifications do not constitute a
          material change requiring re-acceptance under Section 11.5.
        </p>

        <h3 className={styles.subsectionTitle}>3D. Transfer to Adopter (T2A)</h3>

        <p className={styles.clause}>
          <span className={styles.clauseNumber}>3D.1</span> <strong>Completion Benchmark.</strong> Rescue shall maintain
          a T2A completion rate of at least eighty percent (80%), measured as completed T2A transactions divided by
          initiated T2A transactions over a rolling ninety (90)-day period.
        </p>
        <p className={styles.clause}>
          <span className={styles.clauseNumber}>3D.2</span> <strong>Improvement Process.</strong> If Rescue&rsquo;s T2A
          completion rate falls below sixty percent (60%) for ninety (90) or more consecutive days, Fi will issue a
          written improvement notice identifying the deficiency. Rescue shall have thirty (30) days from receipt of such
          notice to cure the deficiency.
        </p>
        <p className={styles.clause}>
          <span className={styles.clauseNumber}>3D.3</span> <strong>Allocation.</strong> Fi may adjust Rescue&rsquo;s Fi
          Device allocation at Fi&rsquo;s sole discretion, and may use completion rate percentages as a factor in such
          determinations.
        </p>
        <p className={styles.clause}>
          <span className={styles.clauseNumber}>3D.4</span> <strong>Adopter Information.</strong> Rescue shall provide
          accurate Adopter contact information (email address and phone number) for each T2A transaction and represents
          that it has permission from Adopter to do so. Incomplete or inaccurate information that prevents Fi from
          completing a transfer counts as an incomplete T2A for purposes of the completion rate calculation.
        </p>
        <p className={styles.clause}>
          <span className={styles.clauseNumber}>3D.5</span> <strong>Adopter Data Use.</strong> Adopter data collected by
          Rescue during T2A shall be used by Rescue solely for the purpose of completing the device and Membership
          transfer. Rescue shall not use Adopter contact information for marketing, fundraising, or any purpose
          unrelated to the T2A transaction without the Adopter&rsquo;s separate consent.
        </p>

        <h3 className={styles.subsectionTitle}>3E. Fi&rsquo;s Commitments</h3>

        <p className={styles.clause}>
          <span className={styles.clauseNumber}>3E.1</span> Fi shall provide Rescue with onboarding materials, training
          resources, and documentation sufficient to enable Rescue to participate effectively in the Program.
        </p>
        <p className={styles.clause}>
          <span className={styles.clauseNumber}>3E.2</span> Fi shall provide Rescue with access to the Portal and the Fi
          mobile app as needed for device management, T2A processing, and inventory tracking.
        </p>
        <p className={styles.clause}>
          <span className={styles.clauseNumber}>3E.3</span> Fi shall provide Rescue with documentation of the T2A
          transfer process and keep such documentation current as the process evolves.
        </p>
        <p className={styles.clause}>
          <span className={styles.clauseNumber}>3E.4</span> Fi shall provide reasonable support to Rescue for
          Program-related inquiries via email or such other channels as Fi may designate.
        </p>

        {/* Section 4 */}
        <h2 className={styles.sectionTitle}>4. Acceptable Use &amp; Restrictions</h2>

        <p className={styles.clause}>
          <span className={styles.clauseNumber}>4.1</span> Rescue shall not:
        </p>
        <ul className={styles.subList}>
          <li>
            (a) Resell, sublease, pledge, or commercially exploit any products or services provided under this Program;
          </li>
          <li>
            (b) Transfer products to unaffiliated organizations, individuals, or entities without Fi&rsquo;s prior
            written consent;
          </li>
          <li>
            (c) Provide false, misleading, or materially incomplete information about animals in Rescue&rsquo;s care,
            Rescue&rsquo;s organizational status, or Rescue&rsquo;s use of Program products;
          </li>
          <li>(d) Attempt to circumvent, disable, or interfere with any security features of the Portal;</li>
          <li>(e) Use any Program products or services in connection with any unlawful activity.</li>
        </ul>

        <p className={styles.clause}>
          <span className={styles.clauseNumber}>4.2</span> Any violation of this Section 4 constitutes a material breach
          of this Agreement and is grounds for immediate termination under Section 10.3.
        </p>

        {/* Section 5 */}
        <h2 className={styles.sectionTitle}>5. Data Privacy</h2>

        <p className={styles.clause}>
          <span className={styles.clauseNumber}>5.1</span> Fi collects, processes, and stores data in accordance with
          the Fi Privacy Policy (available at fitracking.com/privacy), as updated from time to time at Fi&rsquo;s sole
          discretion.
        </p>
        <p className={styles.clause}>
          <span className={styles.clauseNumber}>5.2</span> Rescue shall comply with all applicable federal, state, and
          local privacy and data protection laws in its handling of any personal data received through the Program,
          including Adopter contact information.
        </p>
        <p className={styles.clause}>
          <span className={styles.clauseNumber}>5.3</span> Rescue shall not sell, share, license, or otherwise
          distribute Adopter data to any third party for marketing, advertising, or any purpose unrelated to completing
          T2A transactions, unless Rescue has obtained the Adopter&rsquo;s separate, informed consent.
        </p>
        <p className={styles.clause}>
          <span className={styles.clauseNumber}>5.4</span> Rescue shall notify Fi promptly (and in any event within
          seventy-two (72) hours) of any actual or suspected data breach involving Program data or Adopter personal
          information.
        </p>
        <p className={styles.clause}>
          <span className={styles.clauseNumber}>5.5</span> Upon completion of a T2A transaction, Rescue&rsquo;s access,
          and right to access, to pet location data, activity data, and other telemetry associated with the transferred
          Fi Device shall cease.
        </p>
        <p className={styles.clause}>
          <span className={styles.clauseNumber}>5.6</span> <strong>Fi Data Ownership.</strong> Fi owns and controls all
          data collected through the Fi app, Memberships, and direct consumer interactions. Nothing in this Agreement
          transfers any ownership interest in such data to Rescue.
        </p>
        <p className={styles.clause}>
          <span className={styles.clauseNumber}>5.7</span> <strong>PII Collection Boundary.</strong> Rescue shall not
          collect personal information from Adopters related to their Fi product purchase or Membership beyond what is
          required to complete the T2A transaction. Rescue shall not represent to any third party that it has access to
          pet location, activity, or tracking data following a completed T2A transfer.
        </p>

        {/* Section 6 */}
        <h2 className={styles.sectionTitle}>6. Intellectual Property &amp; Trademark</h2>

        <p className={styles.clause}>
          <span className={styles.clauseNumber}>6.1</span> Fi grants Rescue a limited, non-exclusive, non-transferable,
          revocable license to use Fi trademarks, logos, and brand assets solely in connection with adoption materials,
          Rescue&rsquo;s website, and social media posts directly related to the Program and solely while this Agreement
          is in force; provided that Fi may revoke such rights at any time and Rescue agrees to promptly cease all use.
        </p>
        <p className={styles.clause}>
          <span className={styles.clauseNumber}>6.2</span> Rescue shall use only brand assets provided or approved by Fi
          and shall comply with any brand guidelines Fi provides.
        </p>
        <p className={styles.clause}>
          <span className={styles.clauseNumber}>6.3</span> Rescue shall not:
        </p>
        <ul className={styles.subList}>
          <li>(a) Represent itself as an agent, employee, franchisee, or legal representative of Fi;</li>
          <li>
            (b) Make any warranty, guarantee, or representation on Fi&rsquo;s behalf regarding Fi products or services;
          </li>
          <li>
            (c) Register or attempt to register any trademark, domain name, or social media handle that incorporates
            Fi&rsquo;s trademarks or is associated in any way with Fi&rsquo;s product, services or brand.
          </li>
        </ul>

        <p className={styles.clause}>
          <span className={styles.clauseNumber}>6.4</span> All goodwill arising from Rescue&rsquo;s use of Fi trademarks
          shall inure to Fi&rsquo;s benefit. The licenses set forth in this section terminate immediately upon
          termination of this Agreement.
        </p>

        {/* Section 7 */}
        <h2 className={styles.sectionTitle}>7. Confidentiality</h2>

        <p className={styles.clause}>
          <span className={styles.clauseNumber}>7.1</span> &ldquo;Confidential Information&rdquo; includes, but is not
          limited to: Program pricing and discount structures, program economics, internal Fi metrics shared with
          Rescue, unreleased product information, and any information marked or reasonably understood to be
          confidential.
        </p>
        <p className={styles.clause}>
          <span className={styles.clauseNumber}>7.2</span> Rescue shall not disclose Confidential Information to any
          third party without Fi&rsquo;s prior written consent, except as required by law (in which case Rescue shall
          provide Fi with prompt written notice to the extent legally permitted).
        </p>
        <p className={styles.clause}>
          <span className={styles.clauseNumber}>7.3</span> This Section 7 shall survive termination of this Agreement
          for a period of two (2) years.
        </p>

        {/* Section 8 */}
        <h2 className={styles.sectionTitle}>8. Indemnification</h2>

        <p className={styles.clause}>
          <span className={styles.clauseNumber}>8.1</span> <strong>Rescue Indemnification.</strong> Rescue shall
          indemnify, defend, and hold harmless Fi and its officers, directors, employees, and agents from and against
          any claims, damages, losses, liabilities, costs, and expenses (including reasonable attorneys&rsquo; fees)
          arising out of or related to:
        </p>
        <ul className={styles.subList}>
          <li>(a) Rescue&rsquo;s misuse of products provided under this Program;</li>
          <li>(b) Rescue&rsquo;s breach of this Agreement;</li>
          <li>(c) Rescue&rsquo;s negligence or willful misconduct;</li>
          <li>(d) Any animal welfare violation or allegation of animal abuse or neglect involving Rescue;</li>
          <li>(e) Any unauthorized representation made by Rescue on Fi&rsquo;s behalf.</li>
        </ul>

        <p className={styles.clause}>
          <span className={styles.clauseNumber}>8.2</span> <strong>Fi Indemnification.</strong> Fi shall indemnify,
          defend, and hold harmless Rescue and its officers, directors, employees, and volunteers from and against any
          claims, damages, losses, liabilities, costs, and expenses (including reasonable attorneys&rsquo; fees) arising
          out of or related to:
        </p>
        <ul className={styles.subList}>
          <li>(a) Defects in Fi products that cause bodily injury or property damage;</li>
          <li>
            (b) Fi&rsquo;s violation of its own Privacy Policy with respect to data collected through the Program;
          </li>
          <li>
            (c) Any claim that Fi products or services infringe a third party&rsquo;s intellectual property rights.
          </li>
        </ul>

        <p className={styles.clause}>
          Fi&rsquo;s indemnification obligations under this Section 8.2 shall not apply to the extent a claim arises
          from: (i) Rescue&rsquo;s breach of this Agreement; (ii) Rescue&rsquo;s negligence or intentional misconduct;
          or (iii) Rescue&rsquo;s failure to follow Fi&rsquo;s written instructions regarding use of Program products.
        </p>

        {/* Section 9 */}
        <h2 className={styles.sectionTitle}>9. Limitation of Liability</h2>

        <p className={styles.clause}>
          <span className={styles.clauseNumber}>9.1</span> <strong>Warranty Disclaimer.</strong>{' '}
          <span className={styles.capsText}>
            ALL PRODUCTS AND SERVICES PROVIDED UNDER THIS PROGRAM ARE PROVIDED &ldquo;AS IS&rdquo; AND &ldquo;AS
            AVAILABLE.&rdquo; FI DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED
            WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
          </span>
        </p>
        <p className={styles.clause}>
          <span className={styles.clauseNumber}>9.2</span> <strong>GPS Tracking Disclaimer.</strong> Fi does not warrant
          uninterrupted, error-free, or complete GPS tracking coverage. Fi is not liable for any lost, escaped, injured,
          or deceased animals, regardless of whether a Fi Device was in use at the time. GPS tracking is a supplemental
          tool and does not replace physical containment, supervision, or other animal safety measures.
        </p>
        <p className={styles.clause}>
          <span className={styles.clauseNumber}>9.3</span> <strong>Liability Cap.</strong>{' '}
          <span className={styles.capsText}>
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, FI&rsquo;S AGGREGATE LIABILITY UNDER THIS AGREEMENT SHALL NOT EXCEED
            THE GREATER OF: (A) THE RETAIL VALUE OF PRODUCTS PROVIDED TO PARTNER IN THE TWELVE (12) MONTHS PRECEDING THE
            CLAIM, OR (B) ONE THOUSAND DOLLARS ($1,000).
          </span>
        </p>
        <p className={styles.clause}>
          <span className={styles.clauseNumber}>9.4</span> <strong>Consequential Damages Waiver.</strong>{' '}
          <span className={styles.capsText}>
            IN NO EVENT SHALL EITHER PARTY BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE
            DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF REVENUE, LOSS OF DONATIONS, OR REPUTATIONAL HARM, REGARDLESS
            OF THE CAUSE OF ACTION OR THE THEORY OF LIABILITY.
          </span>
        </p>

        {/* Section 10 */}
        <h2 className={styles.sectionTitle}>10. Term &amp; Termination</h2>

        <p className={styles.clause}>
          <span className={styles.clauseNumber}>10.1</span> <strong>Term.</strong> This Agreement is effective on the
          Effective Date and shall continue for an initial term of one (1) year, automatically renewing for successive
          one (1)-year terms unless terminated in accordance with this Section 10.
        </p>
        <p className={styles.clause}>
          <span className={styles.clauseNumber}>10.2</span> <strong>Termination Without Cause.</strong> Either party may
          terminate this Agreement for convenience by providing thirty (30) days&rsquo; written notice to the other
          party.
        </p>
        <p className={styles.clause}>
          <span className={styles.clauseNumber}>10.3</span> <strong>Immediate Termination (No Cure).</strong> Fi may
          terminate this Agreement immediately, without opportunity to cure, upon the occurrence of any of the
          following:
        </p>
        <ul className={styles.subList}>
          <li>(a) Loss or revocation of Rescue&rsquo;s 501(c)(3) or equivalent nonprofit status;</li>
          <li>(b) Resale, commercial exploitation, or unauthorized distribution of Program products;</li>
          <li>(c) Promo Code abuse as described in Section 3C;</li>
          <li>
            (d) Material misrepresentation of Rescue&rsquo;s organizational status, animal inventory, or program
            metrics;
          </li>
          <li>(e) Substantiated allegation or finding of animal abuse, neglect, or cruelty involving Rescue;</li>
          <li>(f) At Fi&rsquo;s discretion to protect its business interests.</li>
        </ul>

        <p className={styles.clause}>
          <span className={styles.clauseNumber}>10.4</span> <strong>Effects of Termination.</strong> Upon termination
          for any reason:
        </p>
        <ul className={styles.subList}>
          <li>
            (a) Rescue shall return all Fi Devices in its possession within thirty (30) days. Fi shall provide prepaid
            shipping labels;
          </li>
          <li>
            (b) All Promo Codes assigned to Rescue should stop being used by Rescue and Fi is free to deactivate them;
          </li>
          <li>(c) Rescue&rsquo;s Portal access may be revoked by Fi;</li>
          <li>
            (d) Fi Nano Chips already implanted in animals shall remain registered in the Fi Nano database and are not
            subject to return;
          </li>
          <li>(e) Unimplanted Fi Nano Chip stock shall be returned by Rescue to Fi with Fi Devices;</li>
          <li>(f) Rescue&rsquo;s license to use Fi trademarks under Section 6 terminates immediately;</li>
          <li>
            (g) Sections 5 (Data Privacy), 7 (Confidentiality), 8 (Indemnification), 9 (Limitation of Liability), and 11
            (General Provisions) shall survive termination;
          </li>
          <li>
            (h) Fi Devices that have been fully transferred to Adopters via completed T2A transactions prior to
            termination shall remain with those Adopters and are subject to the terms and conditions between Adopters
            and Fi and the termination or non-termination of this Agreement is not relevant to them.
          </li>
        </ul>

        {/* Section 11 */}
        <h2 className={styles.sectionTitle}>11. General Provisions</h2>

        <p className={styles.clause}>
          <span className={styles.clauseNumber}>11.1</span> <strong>Governing Law.</strong> This Agreement shall be
          governed by and construed in accordance with the laws of the State of New York, without regard to its
          conflicts of law provisions.
        </p>
        <p className={styles.clause}>
          <span className={styles.clauseNumber}>11.2</span> <strong>Entire Agreement.</strong> This Agreement
          constitutes the entire agreement between the parties with respect to its subject matter and supersedes all
          prior or contemporaneous communications, representations, or agreements, whether oral or written.
        </p>
        <p className={styles.clause}>
          <span className={styles.clauseNumber}>11.3</span> <strong>Severability.</strong> If any provision of this
          Agreement is held to be invalid or unenforceable, the remaining provisions shall continue in full force and
          effect.
        </p>
        <p className={styles.clause}>
          <span className={styles.clauseNumber}>11.4</span> <strong>Amendments.</strong> Fi may update or modify the
          terms of this Agreement with thirty (30) days&rsquo; written notice to Rescue. Non-material changes (e.g.,
          updates to GWA pricing, product availability) take effect automatically after the notice period. Material
          changes (e.g., modifications to liability, termination, or data privacy terms) require Rescue&rsquo;s
          re-acceptance via the Portal before taking effect. Failure to re-accept within sixty (60) days of a material
          change notice shall be treated as termination without cause by Rescue.
        </p>
        <p className={styles.clause}>
          <span className={styles.clauseNumber}>11.5</span> <strong>Electronic Acceptance.</strong> Rescue acknowledges
          and agrees that electronic acceptance of this Agreement via the Portal constitutes a valid and binding
          signature under the Electronic Signatures in Global and National Commerce Act (E-SIGN Act), the Uniform
          Electronic Transactions Act (UETA), and any applicable state electronic signature laws.
        </p>
        <p className={styles.clause}>
          <span className={styles.clauseNumber}>11.6</span> <strong>No Joint Venture or Agency.</strong> Nothing in this
          Agreement shall be construed to create a joint venture, partnership, franchise, or agency relationship between
          the parties.
        </p>
        <p className={styles.clause}>
          <span className={styles.clauseNumber}>11.7</span> <strong>Assignment.</strong> Rescue may not assign or
          transfer this Agreement or any rights or obligations hereunder without Fi&rsquo;s prior written consent. Fi
          may assign this Agreement without restriction. Any purported assignment in violation of this section is void.
        </p>
        <p className={styles.clause}>
          <span className={styles.clauseNumber}>11.8</span> <strong>Notices.</strong> All notices under this Agreement
          shall be sent to the email addresses on file in the Portal. Notices to Fi must include a copy to
          legal@tryfi.com to be effective. Rescue is responsible for keeping its contact information current.
        </p>
        <p className={styles.clause}>
          <span className={styles.clauseNumber}>11.9</span> <strong>Waiver.</strong> The failure of either party to
          enforce any provision of this Agreement shall not constitute a waiver of that party&rsquo;s right to enforce
          that provision or any other provision in the future.
        </p>
      </div>
    </div>
  );
}
